import { Folder } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import StoresNotFoundModal from "./StoresNotFoundModal";
import CreateStoreModal from "./CreateStoreModal";
import Config from "config";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { State } from "state";
import { deleteProject, fetchProject, updateProject } from "state/project";
import { createAnalyticsEvent, EventTypes, Status } from "state/analytics";
import { updateNotifications } from "state/notifications";
import * as XLSX from "xlsx";
import moment from "moment";
import Step2UploadAttachments from "./Step2UploadAttachments";
import { checkStores } from "state/store";
import { uploadFile } from "state/create_file_upload";
import { deleteFileMappings, deleteMultipleFileMappings, fetchAllProjectFiles } from "state/files";
import { LightThemeTable } from "components/LightThemeTable";
import { buildExportFilename, getTemplateHeaders } from "utils/helpers";
import { Button, IconButton } from "@material-ui/core";
import { GetAppOutlined, CloudUpload } from "@material-ui/icons";
import ProjectCSVDownload from "components/ProjectCSVDownload";
import TextLineLoader from "components/TextLineLoader";
import Step2IndividualUpload from "./Step2IndividualUpload";
import { deleteProjectStores } from "state/project_store_deadline";
import config from "config";


interface Step2FormProps {
  projectId: string;
  project: any;
  updateProject: (id: string, data: any) => Promise<any>;

}

interface CheckStoresResponse {
  availableIds: any[];
  notAvailableIds: any[];
}

type UploadAttachmentsLocalProps = {
  user?: any;
};

type Step2CombinedProps = Step2FormProps &
  RouteComponentProps &
  PropsFromRedux &
  UploadAttachmentsLocalProps;

const Step2Form: React.FC<Step2CombinedProps> = ({ projectId, project, updateProject, user, files }) => {
  const dispatch = useDispatch();
  const userId = user?.id;

  // File states
  const [uploadedFileNames, setUploadedFileNames] = useState<any[]>([]);
  const [uploadedFileStoreIds, setUploadedFileStoreIds] = useState<any[]>([]);

  // UI states
  const [fileStatus, setFileStatus] = useState("");
  const [isTemplateHovered, setIsTemplateHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [unavailableStore, setUnavailableStore] = useState<string[]>([]);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isCsvEnabled, setIsCsvEnabled] = useState(false);
  const [isAttachmentEnabled, setIsAttachmentEnabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceFileType, setReplaceFileType] = useState<string>("");

  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState<any>(null);
  const [uploadModalFlow, setUploadModalFlow] = useState<"idle" | "validated">("idle");
  const uploadAttachmentsRef = useRef<{
    triggerUpload: () => Promise<void>;
    getSelectedFileNames: () => string[];
    clearFiles: () => void;
    highlightMismatchedFiles: (fileNames: string[]) => void;
  } | null>(null);

  // Upload flow states
  const [uploadFlow, setUploadFlow] = useState<
    "idle" | "checking" | "validated" | "uploading" | "paused"
  >("idle");
  const [isStoreUploading, setIsStoreUploading] = useState(false);
  const [storeUploadError, setStoreUploadError] = useState<string>("");
  const [storeUploadSuccess, setStoreUploadSuccess] = useState(false);
  const [selectedStoreFiles, setSelectedStoreFiles] = useState<any[]>([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFilesInStorage, setHasFilesInStorage] = useState(false);
  const [storeFilesPage, setStoreFilesPage] = useState(1);
  const [storeFilesPageSize, setStoreFilesPageSize] = useState(100);

  // File refs
  const storeFileRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract column data from file
  const extractMultipleColumnData = async (
    file: File
  ): Promise<{ fileData: string[]; storeNumberData: string[] }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const data = e.target.result;
          const fileData: string[] = [];
          const storeNumberData: string[] = [];

          if (file.name.endsWith(".csv")) {
            const raw = data as ArrayBuffer;
            const bytes = new Uint8Array(raw);
            let encoding = "utf-8";
            if (bytes[0] === 0xff && bytes[1] === 0xfe) encoding = "utf-16le";
            else if (bytes[0] === 0xfe && bytes[1] === 0xff) encoding = "utf-16be";
            else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
              let valid = true;
              for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
                if (bytes[i] > 0x7f && bytes[i] < 0xa0) { valid = false; break; }
              }
              if (!valid) encoding = "windows-1252";
            }
            const text = new TextDecoder(encoding).decode(raw);
            const lines = text.split("\n").filter((line) => line.trim() !== "");

            if (lines.length > 0) {
              const headers = lines[0].split(",").map((col: string) => col.trim());
              const fileColumnIndex = headers.findIndex((header) => header === "File");
              const storeNumberColumnIndex = headers.findIndex(
                (header) => header === "Store Number"
              );

              for (let i = 1; i < lines.length; i++) {
                const row = lines[i].split(",");

                if (fileColumnIndex !== -1 && row[fileColumnIndex]) {
                  const fileValue = row[fileColumnIndex].trim();
                  if (fileValue) fileData.push(fileValue);
                }

                if (storeNumberColumnIndex !== -1 && row[storeNumberColumnIndex]) {
                  const storeNumberValue = row[storeNumberColumnIndex].trim();
                  if (storeNumberValue) storeNumberData.push(storeNumberValue);
                }
              }
            }
          } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
            const workbook = XLSX.read(data, { type: "binary" });
            const firstSheet = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheet];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
              const headers = jsonData[0] as string[];
              const fileColumnIndex = headers.findIndex((header) => header === "File");
              const storeNumberColumnIndex = headers.findIndex(
                (header) => header === "Store Number"
              );

              for (let i = 1; i < jsonData.length; i++) {
                const row = jsonData[i] as any[];

                if (fileColumnIndex !== -1 && row && row[fileColumnIndex]) {
                  const fileValue = String(row[fileColumnIndex]).trim();
                  if (fileValue) fileData.push(fileValue);
                }

                if (storeNumberColumnIndex !== -1 && row && row[storeNumberColumnIndex]) {
                  const storeNumberValue = String(row[storeNumberColumnIndex]).trim();
                  if (storeNumberValue) storeNumberData.push(storeNumberValue);
                }
              }
            }
          } else {
            reject(new Error("Unsupported file format"));
          }

          const normalize = (value: string) => String(value).trim();
          const uniqueStoreNumberData = Array.from(
            new Set(storeNumberData.map(normalize).filter((value) => value !== ""))
          );

          resolve({ fileData, storeNumberData: uniqueStoreNumberData });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));

      if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        reader.readAsBinaryString(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  useEffect(() => {
    const checkLocalStorage = () => {
      const storageKey = `attachments_${projectId}_${fileStatus}`;
      const savedFileNames = localStorage.getItem(storageKey);

      if (savedFileNames) {
        try {
          const fileNames = JSON.parse(savedFileNames);
          setHasFilesInStorage(Array.isArray(fileNames) && fileNames.length > 0);
        } catch (error) {
          setHasFilesInStorage(false);
        }
      } else {
        setHasFilesInStorage(false);
      }
    };

    checkLocalStorage();

    // Check periodically in case files are added/removed
    const interval = setInterval(checkLocalStorage, 1000);
    return () => clearInterval(interval);
  }, [projectId, fileStatus])

  // Handle validation and upload for attachments
  const handleValidateAndUpload = async () => {
    const attachmentFileNames = uploadAttachmentsRef.current?.getSelectedFileNames() || [];

    if (attachmentFileNames.length === 0) {
      setValidationMessage("Please select attachment files to upload.");
      setShowValidationModal(true);
      return;
    }

    const normalizeFileName = (name: string): string => name.replace(/#/g, '__');

    const normalizedUploadedNames = uploadedFileNames.map(normalizeFileName);
    const normalizedAttachmentNames = attachmentFileNames.map(normalizeFileName);

    const uploadedSet = new Set(normalizedUploadedNames);
    const attachmentSet = new Set(normalizedAttachmentNames);

    const missingInAttachments = normalizedUploadedNames.filter((name) => !attachmentSet.has(name));
    const extraInAttachments = normalizedAttachmentNames.filter((name) => !uploadedSet.has(name));

    if (missingInAttachments.length === 0 && extraInAttachments.length === 0) {
      uploadAttachmentsRef.current?.highlightMismatchedFiles([]);
      try {
        await uploadAttachmentsRef.current?.triggerUpload();
        await dispatch(fetchAllProjectFiles(Number(projectId)));
      } catch (error) {
        setValidationMessage("Upload failed. Please try again.");
        setShowValidationModal(true);
      }
    } else {
      const extraOriginalNames = attachmentFileNames.filter((name, index) =>
        extraInAttachments.includes(normalizedAttachmentNames[index])
      );

      uploadAttachmentsRef.current?.highlightMismatchedFiles(extraOriginalNames);

      // detect case-sensitivity mismatches
      const isCaseMismatch = (missing: string[], extra: string[]): boolean => {
        const missingLower = new Set(missing.map((n) => n.toLowerCase()));
        return extra.some((n) => missingLower.has(n.toLowerCase()));
      };

      const hasCaseMismatch =
        isCaseMismatch(missingInAttachments, extraInAttachments) ||
        isCaseMismatch(extraInAttachments, missingInAttachments);

      // Build pairs of case-conflicting names for display
      const caseMismatchPairs: Array<{ uploaded: string; attachment: string }> = [];
      if (hasCaseMismatch) {
        missingInAttachments.forEach((missingName) => {
          const match = extraInAttachments.find(
            (extraName) =>
              extraName.toLowerCase() === missingName.toLowerCase() &&
              extraName !== missingName
          );
          if (match) {
            // Map back to original (un-normalized) names for display
            const uploadedOriginal =
              uploadedFileNames[normalizedUploadedNames.indexOf(missingName)] ?? missingName;
            const attachmentOriginal =
              attachmentFileNames[normalizedAttachmentNames.indexOf(match)] ?? match;
            caseMismatchPairs.push({ uploaded: uploadedOriginal, attachment: attachmentOriginal });
          }
        });
      }

      let message = "File mismatch:\n";

      // Show case-sensitivity notice first so it's prominent
      if (hasCaseMismatch) {
        message += "Case sensitivity issue detected:\n";
        message += "The following files appear to be the same but differ in letter casing:\n";
        caseMismatchPairs.forEach(({ uploaded, attachment }) => {
          message += `• Store Mapping: "${uploaded}", Attachment: "${attachment}"\n`;
        });
        message +=
          "\nFile names must match exactly, including uppercase and lowercase letters.\n\n";
      }

      if (missingInAttachments.length > 0) {
        const missingOriginalNames = uploadedFileNames.filter((name, index) =>
          missingInAttachments.includes(normalizedUploadedNames[index])
        );
        message += `Files in Store Mapping but missing in Attachments (${missingOriginalNames.length}):\n`;
        message += missingOriginalNames.map((name) => `• ${name}`).join("\n");
        message += "\n\n";
      }

      if (extraInAttachments.length > 0) {
        message += `Files in Attachments but missing in Store Mapping (${extraOriginalNames.length}):\n`;
        message += extraOriginalNames.map((name) => `• ${name}`).join("\n");
      }

      message += "\n\nPlease ensure both sections have the same files before proceeding.";
      setValidationMessage(message);
      setShowValidationModal(true);
    }
  };
  // Handle modal attachment upload complete - validate file name matches and auto-upload
  const handleModalFilesSelected = async (fileNames: string[]) => {
    if (!selectedFileForUpload || fileNames.length === 0) return;

    const normalize = (name: string): string => String(name || "").replace(/#/g, "__");
    const normalizeType = (value: string): string => String(value || "").trim().toLowerCase();

    const selectedType = String(selectedFileForUpload?.status || "");
    const expectedFileName = String(selectedFileForUpload?.filename || "");
    const normalizedExpected = normalize(expectedFileName);
    const normalizedUploaded = fileNames.map((name) => normalize(name));

    const filesApiRows = (files || []).filter(
      (file: any) =>
        file?.maptype === "Store" &&
        normalizeType(file?.status) === normalizeType(selectedType)
    );

    if (filesApiRows.length === 0) {
      setValidationMessage(
        `No files found for File Type: ${selectedType}.`
      );
      setShowValidationModal(true);
      return;
    }

    const filesApiNameSet = new Set(filesApiRows.map((row: any) => normalize(row.filename)));
    const notInFilesApi = normalizedUploaded.filter((name) => !filesApiNameSet.has(name));

    if (notInFilesApi.length > 0) {
      const missingRows = fileNames
        .filter((name) => notInFilesApi.includes(normalize(name)))
        .map((name) => `• ${name} (File Type: ${selectedType})`)
        .join("\n");

      setValidationMessage(
        `File is not matching from list.\n\n` +
        `The following uploaded file(s) are not available in stores files:\n${missingRows}`
      );
      setShowValidationModal(true);
      return;
    }

    if (!normalizedUploaded.includes(normalizedExpected)) {
      const uploadedDisplay = fileNames.join("\n");
      setValidationMessage(
        `File name mismatch!\n\n` +
        `Expected: ${expectedFileName}\n` +
        `File Type: ${selectedType}\n\n` +
        `Uploaded:\n${uploadedDisplay}`
      );
      setShowValidationModal(true);
      return;
    }

    if (uploadAttachmentsRef.current) {
      try {
        await uploadAttachmentsRef.current.triggerUpload();
        await dispatch(fetchAllProjectFiles(Number(projectId)));
        setTimeout(() => {
          setShowUploadModal(false);
          setSelectedFileForUpload(null);
          setUploadModalFlow("idle");
        }, 2000);
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }
  };

  // Handle store file selection
  const handleStoreFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    storeFileRef.current = file;

    try {
      const { fileData, storeNumberData } = await extractMultipleColumnData(file);
      const normalizedStoreIds = Array.from(
        new Set(storeNumberData.map((id) => String(id).trim()).filter((value) => value !== ""))
      );

      setUploadedFileNames(fileData);
      setUploadedFileStoreIds(normalizedStoreIds);

      // Immediately check stores
      setUploadFlow("checking");
      const chainId = project?.chain?.id;
      if (chainId == null) {
        setStoreUploadError("Failed to validate stores: missing project chain information.");
        setUploadFlow("idle");
        return;
      }

      const response = (await dispatch(
        checkStores(normalizedStoreIds, chainId)
      )) as unknown as CheckStoresResponse;

      if (response?.notAvailableIds?.length > 0) {
        setUnavailableStore(response.notAvailableIds);
        setIsModalOpen(true);
        setUploadFlow("idle");
        return;
      }

      // Upload file immediately after store check
      await uploadStoreFile();

    } catch (error) {
      setStoreUploadError(`Failed to read or upload file - ${error instanceof Error ? error?.message : error}`);
      setUploadFlow("idle");
    }
  };

  // Check stores
  const handleCheckStores = async () => {
    setUploadFlow("checking");
    setLoading(true);

    try {
      const normalizedStoreIds = Array.from(
        new Set(uploadedFileStoreIds.map((id) => String(id).trim()).filter((value) => value !== ""))
      );
      const chainId = project?.chain?.id;
      if (chainId == null) {
        setStoreUploadError("Failed to validate stores: missing project chain information.");
        setUploadFlow("idle");
        setLoading(false);
        return;
      }

      const response = (await dispatch(
        checkStores(normalizedStoreIds, chainId)
      )) as unknown as CheckStoresResponse;

      if (response?.notAvailableIds?.length > 0) {
        setUnavailableStore(response.notAvailableIds);
        setIsModalOpen(true);
        setUploadFlow("idle");
      } else {
        setUploadFlow("paused");
      }
    } catch (error) {
      setStoreUploadError(`Failed to check stores - ${error instanceof Error ? error?.message : error}`);
      setUploadFlow("idle");
    }
    setLoading(false)
  };

  const handleStoreFileCheckboxChange = (mapid: number, maptype: any) => {
    const index = selectedStoreFiles.findIndex(
      (file: any) => file.mapid === mapid && file.maptype === maptype
    );

    if (index > -1) {
      const newSelectedFiles = [...selectedStoreFiles];
      newSelectedFiles.splice(index, 1);
      setSelectedStoreFiles(newSelectedFiles);
    } else {
      setSelectedStoreFiles([...selectedStoreFiles, { mapid, maptype }]);
    }
  };

  const handleSelectAllStoreFiles = () => {
    const storeFiles = files?.filter((f: any) => f.maptype === "Store") || [];

    if (selectedStoreFiles.length === storeFiles.length) {
      setSelectedStoreFiles([]);
    } else {
      const allFiles = storeFiles.map((file: any) => ({
        mapid: file.mapid,
        maptype: file.maptype,
      }));
      setSelectedStoreFiles(allFiles);
    }
  };

  const handleBulkDeleteStoreFiles = async () => {
    if (selectedStoreFiles.length === 0) return;
    setIsDeletingBulk(true);

    try {
      const selectedFilesMapIds = selectedStoreFiles.map((item: any) => ({
        mapid: Number(item.mapid),
        maptype: item.maptype
      }));

      await dispatch(deleteMultipleFileMappings(selectedFilesMapIds));
      setSelectedStoreFiles([]);
      await dispatch(fetchAllProjectFiles(Number(projectId)));
      setIsDeletingBulk(false);
    } catch (err) {
      console.error("Bulk delete failed:", err);
      setIsDeletingBulk(false);
    }
  };

  // Handle upload icon click
  const handleUploadClick = (file: any) => {
    setSelectedFileForUpload(file);
    setShowUploadModal(true);
    setUploadModalFlow("idle");
  };

  // Trigger check stores when file IDs are received
  useEffect(() => {
    if (uploadedFileStoreIds.length > 0) {
      handleCheckStores();
    }
  }, [uploadedFileStoreIds]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchAllProjectFiles(Number(projectId)));
    }
  }, [projectId]);


  // Upload store file to API
  const uploadStoreFile = async () => {
    if (!storeFileRef.current) return;

    setIsStoreUploading(true);
    setStoreUploadError("");
    setStoreUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", storeFileRef.current);
      formData.append("projectId", projectId);
      formData.append("file_status", fileStatus);
      formData.append("file_type", "Store");

      if (userId) formData.append("userId", `${userId}`);

      // Append project info if exists
      if (project) {
        formData.append("chain_name", `${project?.chain?.name}`);
        formData.append("project_name", `${project?.name}`);
        formData.append("project_type", `${project?.project_type}`);
        formData.append("project_deadline", `${project?.expires_at}`);
        formData.append("project_timezone", project?.timezone || "");
        formData.append("project_year", `${project?.project_year}`);
        formData.append("season", `${project?.project_season}`);
      }

      const offset = moment().utcOffset();
      const timezone = moment.tz.guess();
      formData.append("offset", `${offset}`);
      formData.append("timezone", timezone);

      await dispatch(
        uploadFile("STORE2POG", `${Config.API_SERVICE_URL}/api/project-stores`, {
          offset,
          timezone,
        })({ data: formData } as any)
      );

      setStoreUploadSuccess(true);

      // Enable attachments after CSV upload
      setIsAttachmentEnabled(true);

      await updateProject(projectId, {});
      await dispatch(fetchAllProjectFiles(Number(projectId)));

      createAnalyticsEvent({
        event: EventTypes.CREATE_NEW_PROJECT_UPLOAD,
        eventType: "Action",
        projectName:
          project?.chain?.name && project?.name ? `${project.chain.name} - ${project.name}` : "",
        projectType: project?.project_type,
        fileUploadType: "New Stores",
        fileUploadStatus: Status.SUCCESS,
      });

      setIsStoreUploading(false);

      // Reset immediately for next upload (success message will still show briefly)
      resetUploadState();

    } catch (error) {
      setStoreUploadError("Failed to upload file");
      setIsStoreUploading(false);
    }
  };

  // Modal handlers
  const handleYes = () => {
    setIsModalOpen(false);
    setIsStoreModalOpen(true);
  };

  const handleNo = () => {
    setIsModalOpen(false);
    setUploadFlow("idle");
  };

  let label = "PROJECT STORE MAPPING";

  const handleDeleteFile = (id: number, maptype: any) => {
    setItemToDelete({ id, maptype });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    await dispatch(deleteFileMappings(itemToDelete.id, itemToDelete.maptype));
    await dispatch(fetchAllProjectFiles(Number(projectId)));

    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };
  const deleteAllMappingStoreFiles = async () => {
    if (!files || files.length === 0) return;

    const mappingFiles = files.filter(
      (file: any) =>
        file.maptype === "Store" &&
        file.ProjectFileStatus === "Mapping"
    );

    if (mappingFiles.length === 0) return;

    const payload = mappingFiles.map((file: any) => ({
      mapid: Number(file.mapid),
      maptype: file.maptype,
    }));

    const storesToDelete = mappingFiles
      .filter((file: any) => file.store_id)
      .map((file: any) => ({
        id: Number(file.id),
        store_id: Number(file.store_id),
        project_id: Number(projectId),
      }))
      .filter(item => Number.isInteger(item.store_id));

    try {
      await dispatch(deleteMultipleFileMappings(payload));
      await dispatch(fetchAllProjectFiles(Number(projectId)));

      // if (storesToDelete.length > 0) {
      await dispatch(deleteProjectStores(Number(projectId), storesToDelete));
      // }
    } catch (error) {
      console.error("Failed to delete mapping files:", error);
    }
  };


  const resetUploadState = () => {
    // Clear the file input to allow selecting the same or different file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Clear the stored file reference
    storeFileRef.current = null;

    // Reset upload-related states but keep the file lists for display
    setUploadFlow("idle");
    setIsStoreUploading(false);
    setStoreUploadError("");
    setStoreUploadSuccess(false);

    // Don't reset these - they should persist to show what was uploaded:
    // - uploadedFileNames
    // - uploadedFileStoreIds
    // - isAttachmentEnabled (keep it enabled for subsequent uploads)
  };

  const handleReplaceSelected = () => {
    setReplaceFileType("");
  };

  // Handle bulk replace upload
  const handleBulkReplaceFilesSelected = async (fileNames: string[]) => {
    if (!replaceFileType) {
      setValidationMessage("Please select File Type (Original or Final) before uploading.");
      setShowValidationModal(true);
      return;
    }

    if (!fileNames || fileNames.length === 0) {
      setValidationMessage("Please select at least one file to upload.");
      setShowValidationModal(true);
      return;
    }

    const normalize = (name: string) => name.replace(/#/g, "__");
    const normalizeType = (value: string) => String(value || "").trim().toLowerCase();
    const normalizedUploaded = fileNames.map(normalize);

    const filesApiRows = (files || []).filter(
      (file: any) =>
        file?.maptype === "Store" &&
        normalizeType(file?.status) === normalizeType(replaceFileType)
    );

    if (filesApiRows.length === 0) {
      setValidationMessage(
        `No files found for File Type: ${replaceFileType}.`
      );
      setShowValidationModal(true);
      return;
    }

    const filesApiNameSet = new Set(filesApiRows.map((row: any) => normalize(row.filename)));
    const notInFilesApi = normalizedUploaded.filter((name) => !filesApiNameSet.has(name));

    if (notInFilesApi.length > 0) {
      const missingRows = fileNames
        .filter((name) => notInFilesApi.includes(normalize(name)))
        .map((name) => `• ${name} (File Type: ${replaceFileType})`)
        .join("\n");

      setValidationMessage(
        `File is not matching from list.\n\n` +
        `The following uploaded file(s) are not available in stores files :\n${missingRows}`
      );
      setShowValidationModal(true);
      return;
    }

    try {
      await uploadAttachmentsRef.current?.triggerUpload();
      await dispatch(fetchAllProjectFiles(Number(projectId)));

      setShowReplaceModal(false);
      setReplaceFileType("");
    } catch (err) {
      setValidationMessage("Bulk replace upload failed. Please try again.");
      setShowValidationModal(true);
    }
  };
  const getSelectedStoreRows = () => {
    return selectedStoreFiles
      .map((selected) =>
        files?.find(
          (file: any) =>
            file.mapid === selected.mapid &&
            file.maptype === selected.maptype
        )
      )
      .filter(Boolean);
  };

  const handleDownloadClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    file: any
  ) => {
    e.preventDefault();

    if (!file?.id) {
      console.error("No file URL available for download");
      return;
    }
    // window.open(file.blob_url, "_blank");
    return window.open(`${config.API_SERVICE_URL}/api/file/${file.id}`);
  };
  const storeFiles = files?.filter((f: any) => f.maptype === "Store") || [];
  const allStoreFilesSelected = storeFiles.length > 0 &&
    selectedStoreFiles.length === storeFiles.length;

  const storeFileColumns = [
    {
      field: "checkbox",
      header: (
        <div
          style={{
            position: "relative",
            width: 16,
            height: 16,
            top:4,
            left:2,
            border: `2px solid ${allStoreFilesSelected ? "#E5B611" : "#ccc"}`,
            borderRadius: 3,
            backgroundColor: allStoreFilesSelected ? "#E5B611" : "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
          }}
          onClick={handleSelectAllStoreFiles}
        >
          {allStoreFilesSelected && (
            <svg
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
            >
              <path
                d="M1 4L3.5 6.5L9 1"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      ),
      width: "40px",
      formatter: (_: any, row: any) => {
        const isSelected = selectedStoreFiles.some(
          (file: any) => file.mapid === row.mapid && file.maptype === row.maptype
        );
        return (
          <div
            style={{
              position: "relative",
              width: 16,
              height: 16,
              border: `2px solid ${isSelected ? "#E5B611" : "#ccc"}`,
              borderRadius: 3,
              backgroundColor: isSelected ? "#E5B611" : "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onClick={() => handleStoreFileCheckboxChange(row.mapid, row.maptype)}
          >
            {isSelected && (
              <svg
                width="10"
                height="8"
                viewBox="0 0 10 8"
                fill="none"
              >
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        );
      },
    },
    {
      field: "filename",
      header: "File Name",
      formatter: (_: any, row: any) => (
        <div >{row.filename}</div>
      ),
    },
    {
      field: "retail_store_num",
      header: "Store No",
      formatter: (_: any, row: any) => (
        <div>{row.retail_store_num ?? "-"}</div>
      ),
    },
    {
      field: "updated_at",
      header: "Uploaded Date",
      formatter: (_: any, row: any) => {
        const uploadedAt = row?.updated_at || row?.created_at;
        return (
          <div >
            {uploadedAt ? moment(uploadedAt).format("MM-DD-YYYY") : "-"}
          </div>
        );
      },
    },
    {
      field: "status",
      header: "File Status",
      formatter: (_: any, row: any) => (
        <div >
          {row.status ? row.status : '-'}
        </div>
      ),
    },
    {
      field: "ProjectFileStatus",
      header: "Status",
      formatter: (_: any, row: any) => (
        <div >
          {row.ProjectFileStatus ? row.ProjectFileStatus : '-'}
        </div>
      ),
    },
    {
      field: "actions",
      header: "Action",
      width: "100px",
      formatter: (_: any, row: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={require("../../assets/img/Delete.png")}
            alt="delete"
            style={{
              cursor: "pointer",
              width: 26,
              height: 26,
              padding: 4,
              borderRadius: "50%",
              objectFit: "contain",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#ffe5e5")}
            onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
            onClick={() => handleDeleteFile(row.mapid, row.maptype)}
          />
          {row?.ProjectFileStatus === 'Upload_Complete' && (
            <IconButton
              size="small"
              style={{ padding: 4 }}
              onClick={(e) => handleDownloadClick(e, row)}
              title="Download File"
            >
              <GetAppOutlined style={{ color: "#E5B611", fontSize: 20 }} />
            </IconButton>
          )}
          {row?.ProjectFileStatus === 'Upload_Complete' && (<IconButton
              size="small"
              style={{ padding: 4 }}
              onClick={() => handleUploadClick(row)}
              title="Replace File"
            >
              <CloudUpload style={{ color: "#E5B611", fontSize: 20 }} />
          </IconButton>)}
        </div>
      ),
    }
  ];

  const uniqueIds = [...new Set(uploadedFileStoreIds)];

  return (
    <div style={styles.step2Form}>
      <div style={{...styles.header, flexWrap: "wrap"}}>
        <h3 style={styles.title}>Store Mapping Files</h3>
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
          <select
            style={styles.fileStatusSelect}
            value={fileStatus}
            onChange={(e) => {
              setFileStatus(e.target.value);
              // Enable CSV only if a valid status is selected
              setIsCsvEnabled(e.target.value !== "File Status");
              // Reset attachment enablement on status change
              if (uploadedFileNames.length === 0) {
                setIsAttachmentEnabled(false);
              }
              resetUploadState();
            }}
          >
            <option style={styles.optionText} value="" disabled hidden>
              File Status
            </option>
            <option style={styles.optionText} value="Original">
              Original
            </option>
            <option style={styles.optionText} value="Final">
              Final
            </option>
          </select>
        </div>

        <div
          style={{
            ...styles.downloadLink,
            textDecoration: "underline"
          }}
        >
          <ProjectCSVDownload
            data={[]}
            headers={getTemplateHeaders(label)}
            filename={buildExportFilename("csv", `${label}-template`)}
          >
            <div className="file-upload-header">Download CSV Template</div>
          </ProjectCSVDownload>
        </div>
      </div>

      <div style={styles.uploadSection}>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => handleStoreFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <div
            onClick={() => isCsvEnabled && fileInputRef.current?.click()}
            onDrop={(e) => {
              if (!isCsvEnabled) return;
              e.preventDefault();
              handleStoreFileSelect(e.dataTransfer.files);
            }}
            onDragOver={(e) => e.preventDefault()}
            style={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              padding: "40px",
              textAlign: "center",
              cursor: isCsvEnabled ? "pointer" : "not-allowed",
              marginBottom: "20px",
              backgroundColor: isCsvEnabled ? "#FCF9EE" : "#f0f0f0",
              transition: "all 0.3s",
            }}
          >
            <Folder size={32} style={{ margin: "0 auto 10px", color: "#ccc" }} />
            <p>
              <span style={{ color: "#E5B42F", marginRight: 5 }}>Click here</span>
              {isCsvEnabled ? "to upload or drag Mapping files" : "Select file status first"}
            </p>
          </div>
          {loading && (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 10,
              marginBottom: 5
            }}>
            <span style={{ fontSize: 12 }}>
              Checking stores...
              <TextLineLoader />
            </span>

              {uploadedFileNames.length > 0 && !isStoreUploading && (
                <button
                  onClick={() => {
                    resetUploadState();
                    setUploadedFileNames([]);
                    setUploadedFileStoreIds([]);
                    setLoading(false);
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#E5B611",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    color: "black",
                  }}
                >
                  Remove File
                </button>
              )}
            </div>
          )}

          {!loading && uploadedFileNames.length > 0 && !isStoreUploading && (
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 5
            }}>
              <button
                onClick={async () => {
                  // 1. Delete all mapping files
                  await deleteAllMappingStoreFiles();

                  // 2. Reset local upload state
                  resetUploadState();
                  setUploadedFileNames([]);
                  setUploadedFileStoreIds([]);
                  setLoading(false);
                }}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#E5B611",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "black",
                }}
              >
                Remove File
              </button>
            </div>
          )}

          {uploadedFileNames.length > 0 && !isStoreUploading && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                marginBottom: 15,
              }}
            >
              <p style={{ margin: "0 0 5px 0", fontWeight: 500 }}>
                Selected: {storeFileRef.current?.name ?? ""}{" "}
              </p>
              <p style={{ margin: "0", fontSize: "12px", color: "#666" }}>
                Files: {uploadedFileNames.length} | Stores: {uniqueIds.length}
              </p>
            </div>
          )}

          {isStoreUploading && (
            <div style={{ padding: "10px", color: "#E5B611", marginTop: "10px" }}>Uploading...</div>
          )}

          {storeUploadError && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#ffebee",
                borderRadius: "4px",
                color: "#c62828",
                marginTop: "10px",
              }}
            >
              {storeUploadError}
            </div>
          )}

          {storeUploadSuccess && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#e8f5e9",
                borderRadius: "4px",
                color: "#2e7d32",
                marginTop: "10px",
              }}
            >
              ✓ Store file uploaded successfully
            </div>
          )}
        </div>

        <div style={{ ...styles.header, marginBottom: "0px" }}>
          <h3 style={styles.title}>Planogram or PDF Files</h3>

          <p style={styles.hiddendownloadLink}>Planogram or PDF Files</p>
        </div>
        <div
          style={{
            pointerEvents: isAttachmentEnabled ? "auto" : "none",
            opacity: isAttachmentEnabled ? 1 : 0.6,
            cursor: isAttachmentEnabled ? "pointer" : "not-allowed",
            transition: "all 0.3s",
            margin: '0px'
          }}
        >
          {/* ATTACHMENT UPLOAD */}
          <Step2UploadAttachments
            ref={uploadAttachmentsRef}
            projectId={Number(projectId)}
            userId={userId}
            fileStatus={fileStatus}
            disabled={!isAttachmentEnabled}
          />
          {isAttachmentEnabled && uploadedFileNames.length > 0 && hasFilesInStorage && (
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleValidateAndUpload}
                disabled={!isAttachmentEnabled}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#E5B611",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isAttachmentEnabled ? "pointer" : "not-allowed",
                  fontSize: "12px",
                  color: "black",
                  opacity: isAttachmentEnabled ? 1 : 0.6,
                  transition: "all 0.2s",
                  fontWeight:600
                }}
              >
                Upload Attachments
              </button>
            </div>
          )}
        </div>
        {files && files.length > 0 && (
         <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: 8 }}>
              <p style={{ fontSize: 12, fontWeight: 600, margin: 0, whiteSpace: "nowrap", flexShrink: 0 }}>
                Uploaded Stores Files:
              </p>

              
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flex: 1,
                    gap: "8px",
                  }}
                >
                  {/* Delete Selected */}
                  {selectedStoreFiles.length > 0 && (<Button
                    variant="contained"
                    onClick={handleBulkDeleteStoreFiles}
                    disabled={isDeletingBulk}
                    style={{
                      fontSize: "12px",
                      borderRadius: "4px",
                      background: "#E5B611",
                      color: "#000",
                      padding: "6px 12px",
                      opacity: isDeletingBulk ? 0.6 : 1,
                    }}
                  >
                    {isDeletingBulk
                      ? "Deleting..."
                      : `Delete Selected (${selectedStoreFiles.length})`}
                  </Button>)}

                  <Button
                    variant="contained"
                    onClick={() => {
                      handleReplaceSelected();
                      setShowReplaceModal(true);
                    }}
                    style={{
                      fontSize: "12px",
                      borderRadius: "4px",
                      background: "#E5B611",
                      color: "#000",
                      padding: "6px 12px",
                      opacity: 1,
                      cursor: "pointer",
                    }}
                  >
                    Replace Files
                  </Button>
                </div>
              {/* )} */}
            </div>
            <LightThemeTable
              data={files.filter((file: any) => file.maptype === "Store")}
              columns={storeFileColumns}
              currentPage={storeFilesPage}
              defaultPageSize={storeFilesPageSize}
              onPageChange={(page: number, pageSize: number) => { setStoreFilesPage(page); setStoreFilesPageSize(pageSize); }}
            />
          </div>
        )}
      </div>

      {/* Stores Not Found Modal */}
      <StoresNotFoundModal
        isOpen={isModalOpen}
        stores={unavailableStore}
        onYes={handleYes}
        onNo={handleNo}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Create Store Modal */}
      <CreateStoreModal
        isOpen={isStoreModalOpen}
        onClose={() => setIsStoreModalOpen(false)}
        onSubmit={async () => {
          // Close the modal
          setIsStoreModalOpen(false);

          // After store creation, re-check stores to allow upload
          // This preserves the CSV file data and allows proceeding with upload
          if (uploadedFileStoreIds.length > 0) {
            setLoading(true);
            const response = (await dispatch(
              checkStores(uploadedFileStoreIds, project.chain.id)
            )) as unknown as CheckStoresResponse;

            setLoading(false);

            // If all stores now exist, proceed to upload automatically
            if (response?.notAvailableIds?.length === 0) {
              await uploadStoreFile();
            } else if (response?.notAvailableIds?.length > 0) {
              // If there are still missing stores, show the modal again
              setUnavailableStore(response.notAvailableIds);
              setIsModalOpen(true);
            }
          }
        }}
        projectId={projectId}
      />
      {/* Upload Modal for upload operations on mapping files */}
      {showUploadModal && selectedFileForUpload && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => {
            setShowUploadModal(false);
            setSelectedFileForUpload(null);
            setUploadModalFlow("idle");
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "650px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, color: "#333", fontSize: "18px" }}>Upload File</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFileForUpload(null);
                  setUploadModalFlow("idle");
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  padding: "0 8px",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#666" }}>
                File Name:
              </p>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
                {selectedFileForUpload.filename}
              </p>
            </div>

            <Step2IndividualUpload
              ref={uploadAttachmentsRef}
              projectId={Number(projectId)}
              userId={userId}
              fileStatus={selectedFileForUpload?.status || fileStatus}
              onFilesUploaded={handleModalFilesSelected}
              shouldTriggerUpload={false}
              disabled={false}
            />
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowValidationModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "650px",
              width: "90%",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", color: "#d32f2f" }}>File Validation Failed</h3>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                fontSize: "14px",
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px",
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              {validationMessage}
            </pre>
            <button
              onClick={() => setShowValidationModal(false)}
              style={{
                marginTop: "16px",
                padding: "10px 20px",
                backgroundColor: "#d32f2f",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "320px",
              fontSize: "12px",
            }}
          >
            <h3 style={{ marginBottom: "10px", fontSize: "12px", fontWeight: 600 }}>
              Are you sure?
            </h3>

            <p style={{ marginBottom: "20px", fontSize: "12px", color: "#555" }}>
              You want to delete this mapping?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={cancelDelete}
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#E5B611",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#E5B611",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {showReplaceModal && !showValidationModal && (() => {
        return (
          <div
            onClick={() => {
              setShowReplaceModal(false);
              setReplaceFileType("");
            }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "650px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
              }}
            >
              {/* Header (same as Upload File modal) */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h3 style={{ margin: 0, color: "#333", fontSize: "18px" }}>
                  Replace Store Files
                </h3>

                <button
                  onClick={() => {
                    setShowReplaceModal(false);
                    setReplaceFileType("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#666",
                    padding: "0 8px",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Selected files count */}
              {/* <p style={{ fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
                Selected Files ({selectedRows.length})
              </p> */}

              {/* Selected files list */}
              {/* <div
                style={{
                  maxHeight: shouldScroll ? "180px" : "unset",
                  overflowY: shouldScroll ? "auto" : "visible",
                  paddingRight: shouldScroll ? "6px" : "0px",
                  marginBottom: "16px",
                }}
              >
                {selectedRows.map((row: any, index: number) => (
                  <div
                    key={row.mapid}
                    style={{
                      fontSize: "12px",
                      backgroundColor: "#f5f5f5",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      marginBottom: "6px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {index + 1}. {row.filename}
                  </div>
                ))}
              </div> */}

              {/* Upload component */}
              <div style={{ marginBottom: "8px" }}>
                <p style={{ margin: "0 0 6px 0", fontSize: "12px", fontWeight: 500 }}>
                  File Type
                </p>
                <select
                  style={{ ...styles.fileStatusSelect, width: "220px" }}
                  value={replaceFileType}
                  onChange={(e) => setReplaceFileType(e.target.value)}
                >
                  <option style={styles.optionText} value="" disabled hidden>
                    File Type
                  </option>
                  <option style={styles.optionText} value="Original">
                    Original
                  </option>
                  <option style={styles.optionText} value="Final">
                    Final
                  </option>
                </select>
              </div>

              <Step2IndividualUpload
                ref={uploadAttachmentsRef}
                projectId={Number(projectId)}
                userId={userId}
                fileStatus={replaceFileType}
                onFilesUploaded={handleBulkReplaceFilesSelected}
                shouldTriggerUpload={false}
                disabled={!replaceFileType}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const connector = connect(
  (state: State) => ({
    project: state.project.content,
    files: state.files.content?.data
  }),
  {
    fetchProject,
    deleteProject,
    updateProject,
    updateNotifications,
    createAnalyticsEvent,
    fetchAllProjectFiles,
    deleteMultipleFileMappings
  }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(Step2Form));

const styles = {
  step2Form: {
    // padding: "20px",
    fontFamily: "Arial, sans-serif",
    fontSize: '12px'
  },
  step2Header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  step2HeaderH2: {
    margin: 0,
    color: "#333",
  },
  fileStatusSelect: {
    width: "200px",
    padding: "8px 10px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    borderRadius: "16px",
    color: "#929497"
    // textAlign: "start",
  },
  optionText: {
    color: "#929497",
    backgroundColor: "#f9f9f9",
    padding: "5px",
    fontSize: '12px'
  },
  value: {
    // Style for the default/placeholder option
    color: "#999",
    // fontWeight: "normal",
  },
  fileStatusContainer: {
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: "orange",
  },
  downloadTemplate: {
    color: "#007bff",
    textDecoration: "none",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  downloadTemplateHover: {
    color: "#0056b3",
    textDecoration: "underline",
  },
  uploadSection: {
    // marginBottom: "30px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
  },
  title: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    margin: 0,
  },
  downloadLink: {
    color: "#f59e0b",
    fontSize: "14px",
    textDecoration: "none",
    fontWeight: 500,
    cursor: "pointer",
  },

  hiddendownloadLink: {
    color: "transparent",
    fontSize: "14px",
    textDecoration: "none",
    fontWeight: 500,
    cursor: "normal",
    Visibility: "hidden",
  },
};
